import MUITooltip, { type TooltipProps as MUITooltipProps } from '@mui/material/Tooltip';
import { styled } from '../../utils/styled';
import Grid from '../Grid';
import Typography from '../Typography';
import { type TooltipKind } from './types';

/** Default tooltip surface width for multiline tooltips using `content` when the `width` prop is omitted. */
export const DEFAULT_RICHTEXT_TOOLTIP_WIDTH = '440px';
export const TooltipTitleContainer = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  lineHeight: theme.typography.body1.lineHeight,
  fontSize: '12px',
  fontWeight: theme.typography.body1.fontWeight,
  color: theme.tokens.foreground.main.inverse
}));
export const TooltipBodyContainer = styled(Grid)(({
  theme
}) => ({
  fontSize: theme.typography.fontSize,
  lineHeight: theme.typography.body1.lineHeight,
  display: 'flex',
  alignItems: 'center',
  color: theme.tokens.foreground.main.inverse
}));

/** Default inverse text for custom tooltip content. Override with styled Typography or a `color` prop. */
export const TooltipTypography = styled(Typography)(({
  theme
}) => ({
  color: theme.tokens.foreground.main.inverse
}));
export const TooltipContainer = styled(Grid, {
  shouldForwardProp: prop => prop !== 'titleBodyGap'
})<{
  kind: TooltipKind;
  titleBodyGap?: string;
}>(({
  kind,
  titleBodyGap,
  theme
}) => ({
  padding: kind === 'singleline' ? '8px' : '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: titleBodyGap ?? (kind === 'singleline' ? '8px' : '16px'),
  width: '100%',
  color: theme.tokens.foreground.main.inverse
}));
const getTooltipWidth = (kind: TooltipKind): string => {
  if (kind === 'singleline') return 'fit-content';
  return '228px';
};
export const StyledTooltip = styled((props: MUITooltipProps) => <MUITooltip classes={{
  popper: props.className
}} {...props} />, {
  shouldForwardProp: prop => prop !== 'allowOverflow' && prop !== 'maxWidth' && prop !== 'maxHeight' && prop !== 'kind' && prop !== 'width'
})<{
  kind: TooltipKind;
  width?: string;
  maxHeight?: string;
  maxWidth?: string;
  allowOverflow?: boolean;
}>(({
  theme,
  kind,
  width,
  maxHeight,
  maxWidth,
  allowOverflow
}) => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: theme.tokens.background.main.inverse.default,
    borderRadius: kind === 'singleline' ? theme.borderRadius.xSmall : theme.borderRadius.xLarge,
    width: width ?? getTooltipWidth(kind),
    ...(allowOverflow === true && {
      overflowY: 'auto',
      overflowX: 'hidden'
    }),
    maxHeight,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: '0px',
    gap: '10px',
    border: `1px solid ${theme.tokens.background.tonal.soft.inverse.default}`,
    maxWidth: maxWidth ?? 'none',
    wordBreak: 'break-word',
    pointerEvents: 'auto',
    color: theme.tokens.foreground.main.inverse,
    // Links inside tooltip content sit on the inverse surface, so they need the inverse link colour
    '& a': {
      color: theme.tokens.foreground.main.linkInverse
    }
  }
}));
export const RichHeaderContainer = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  paddingBottom: '16px',
  borderBottom: `1px solid ${theme.tokens.background.tonal.soft.inverse.default}`,
  width: '100%'
}));
export const RichHeaderTextContent = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0 0',
  overflow: 'hidden'
}));
export const RichCaptionContainer = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '8px',
  color: theme.tokens.foreground.main.inverse
}));
export const RichMetricRowContainer = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
  width: '100%'
}));
export const RichMetricTitleWrapper = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flex: '1 0 0',
  gap: '8px',
  alignItems: 'center'
}));
export const RichMetricValueWrapper = styled('div')(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  color: theme.tokens.foreground.main.inverse,
  whiteSpace: 'nowrap',
  textAlign: 'right'
}));
export const RichBannerContainer = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '8px 12px',
  backgroundColor: theme.tokens.background.tonal.superSoft.inverse.default,
  borderRadius: theme.borderRadius.small,
  width: '100%',
  gap: 0
}));
export const RichBannerHeader = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: '8px'
}));